import { ForgeApp, ForgeAppSetting } from '@/resources/forge'
import Vue from 'vue'

const store = {
  namespaced: true,
  
  state: {
    availableApps: [],
    installedApps: [],
    loading: false,
    error: null,
    lastFetch: null
  },
  
  mutations: {
    SET_AVAILABLE_APPS(state, apps) {
      state.availableApps = apps
    },
    SET_INSTALLED_APPS(state, apps) {
      state.installedApps = apps
    },
    SET_LOADING(state, loading) {
      state.loading = loading
    },
    SET_ERROR(state, error) {
      state.error = error
    },
    ADD_INSTALLED_APP(state, app) {
      state.installedApps.push(app)
    },
    UPDATE_INSTALLED_APP(state, updatedApp) {
      const index = state.installedApps.findIndex(app => app.id === updatedApp.id)
      if (index !== -1) {
        state.installedApps.splice(index, 1, updatedApp)
      }
    },
    REMOVE_INSTALLED_APP(state, appId) {
      state.installedApps = state.installedApps.filter(app => app.id !== appId)
    },
    SET_LAST_FETCH(state, timestamp) {
      state.lastFetch = timestamp
    }
  },
  
  actions: {
    async fetchAvailableApps({ commit, state }) {
      // Check cache (5 minutes)
      const CACHE_DURATION = 5 * 60 * 1000
      if (state.lastFetch && Date.now() - state.lastFetch < CACHE_DURATION) {
        return state.availableApps
      }
      
      commit('SET_LOADING', true)
      try {
        const response = await ForgeApp.get()
        // API still returns wrapped in forge_apps
        commit('SET_AVAILABLE_APPS', response.body.forge_apps || [])
        commit('SET_LAST_FETCH', Date.now())
      } catch (error) {
        console.warn('Failed to fetch available Forge Apps:', error)
        commit('SET_ERROR', error.message || 'Failed to fetch available apps')
        // Don't throw error - just log it and continue with empty array
        commit('SET_AVAILABLE_APPS', [])
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async fetchInstalledApps({ commit }, siteId) {
      commit('SET_LOADING', true)
      try {
        const response = await ForgeAppSetting.get({ site_id: siteId })
        // API still returns wrapped in forge_app_settings
        commit('SET_INSTALLED_APPS', response.body.forge_app_settings || [])
      } catch (error) {
        console.warn('Failed to fetch installed Forge Apps:', error)
        commit('SET_ERROR', error.message || 'Failed to fetch installed apps')
        // Don't throw error - just log it and continue with empty array
        commit('SET_INSTALLED_APPS', [])
      } finally {
        commit('SET_LOADING', false)
      }
    },
    
    async installApp({ commit }, { appId, siteId, config = {} }) {
      try {
        // Use VueResource with explicit URL construction
        const url = `internal_api/sites/${siteId}/forge_app_settings`
        
        // Ensure proper data types and structure
        const requestData = {
          forge_app_setting: {
            forge_app_id: parseInt(appId, 10), // Ensure it's a number
            enabled: true,
            config: config || {} // Ensure config is always an object
          }
        }
        
        // Use Vue.http directly to ensure proper request body handling
        const response = await Vue.http.post(url, requestData)
        
        // Handle both wrapped and unwrapped responses
        const appSetting = response.body.forge_app_setting || response.body
        commit('ADD_INSTALLED_APP', appSetting)
        return appSetting
      } catch (error) {
        console.error('Failed to install Forge App:', error)
        console.error('Error details:', {
          status: error.status,
          statusText: error.statusText,
          body: error.body,
          url: error.url,
          message: error.message
        })
        
        // Log the full error response if available
        if (error.body) {
          console.error('Error response body:', JSON.stringify(error.body, null, 2))
        }
        
        commit('SET_ERROR', error.message || 'Failed to install app')
        throw error
      }
    },
    
    async updateAppSetting({ commit, state }, { siteId, appId, enabled, config }) {
      try {
        const appSetting = state.installedApps.find(
          app => app.forge_app_id === appId
        )
        
        if (appSetting) {
          // Update existing setting
          const url = `internal_api/sites/${siteId}/forge_app_settings/${appSetting.id}`
          const requestData = {
            forge_app_setting: { enabled, config }
          }
          const response = await Vue.http.put(url, requestData)
          // Handle both wrapped and unwrapped responses
          const updatedAppSetting = response.body.forge_app_setting || response.body
          commit('UPDATE_INSTALLED_APP', updatedAppSetting)
          return updatedAppSetting
        } else {
          // Create new setting
          const url = `internal_api/sites/${siteId}/forge_app_settings`
          const requestData = {
            forge_app_setting: { forge_app_id: appId, enabled, config }
          }
          const response = await Vue.http.post(url, requestData)
          // Handle both wrapped and unwrapped responses
          const newAppSetting = response.body.forge_app_setting || response.body
          commit('ADD_INSTALLED_APP', newAppSetting)
          return newAppSetting
        }
      } catch (error) {
        console.error('Failed to update Forge App setting:', error)
        commit('SET_ERROR', error.message || 'Failed to update app setting')
        throw error
      }
    },
    
    async uninstallApp({ commit }, { siteId, appSettingId }) {
      try {
        const url = `internal_api/sites/${siteId}/forge_app_settings/${appSettingId}`
        await Vue.http.delete(url)
        commit('REMOVE_INSTALLED_APP', appSettingId)
      } catch (error) {
        console.error('Failed to uninstall Forge App:', error)
        commit('SET_ERROR', error.message || 'Failed to uninstall app')
        throw error
      }
    },
    
    async testAppSnippet({ commit }, { appId, config }) {
      try {
        const response = await ForgeApp.testSnippet({ id: appId }, { config })
        // API returns the object directly for test endpoints
        return response.body
      } catch (error) {
        console.error('Failed to test Forge App snippet:', error)
        commit('SET_ERROR', error.message || 'Failed to test snippet')
        throw error
      }
    },
    
    async testAppSettingSnippet({ commit }, { siteId, appSettingId }) {
      try {
        const url = `internal_api/sites/${siteId}/forge_app_settings/${appSettingId}/test_snippet`
        const response = await Vue.http.post(url)
        // API returns the object directly for test endpoints
        return response.body
      } catch (error) {
        console.error('Failed to test Forge App setting snippet:', error)
        commit('SET_ERROR', error.message || 'Failed to test snippet')
        throw error
      }
    }
  },
  
  getters: {
    getAppById: (state) => (id) => {
      return state.availableApps.find(app => app.id === id)
    },
    getInstalledAppById: (state) => (id) => {
      return state.installedApps.find(app => app.forge_app_id === id)
    },
    appsByCategory: (state) => {
      const categories = {}
      state.availableApps.forEach(app => {
        if (!categories[app.category]) {
          categories[app.category] = []
        }
        categories[app.category].push(app)
      })
      return categories
    },
    isAppInstalled: (state) => (appId) => {
      return state.installedApps.some(app => app.forge_app_id === appId)
    }
  }
}

export default store 